import { useState } from "react";
import { Plus, Edit3, Trash2, Check, Wallet } from "lucide-react";
import { Card } from "./Card";
import { Btn } from "./Btn";
import { Badge } from "./Badge";
import { toast } from "./Toast";
import { useAppData } from "../../lib/AppDataContext";
import { inr } from "../../lib/attendance";

/* Salary advances panel — admin records partial salary withdrawn by reps
   mid-month. Sits below the monthly calendar in Attendance →
   Monthly Calendar. */
export function SalaryAdvancesPanel() {
  const { salaryAdvances, addSalaryAdvance, updateSalaryAdvance, deleteSalaryAdvance, month, allSalesPeople } = useAppData();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({
    employeeId: allSalesPeople[0]?.id || "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    note: "",
  });
  const reset = () => setDraft({
    employeeId: allSalesPeople[0]?.id || "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    note: "",
  });
  const startAdd = () => { setEditing(null); reset(); setAdding(true); };
  const startEdit = (e) => {
    setEditing(e);
    const emp = allSalesPeople.find((s) => s.id === e.employeeId || s.name === e.employeeName);
    setDraft({ employeeId: emp?.id || "", amount: String(e.amount), date: e.date, note: e.note || "" });
    setAdding(true);
  };
  const cancel = () => { setAdding(false); setEditing(null); reset(); };
  const submit = async () => {
    const amt = parseFloat(draft.amount);
    if (!draft.employeeId || !amt || amt <= 0) { toast("Pick an employee and an amount > 0"); return; }
    try {
      if (editing) {
        await updateSalaryAdvance(editing.id, { amount: amt, date: draft.date, note: draft.note.trim() });
        toast("Advance updated");
      } else {
        await addSalaryAdvance({ employeeId: draft.employeeId, amount: amt, date: draft.date, note: draft.note.trim() });
        toast("Advance recorded");
      }
      cancel();
    } catch (e) {
      toast("Save failed: " + (e?.message || e), "error");
    }
  };

  const totalsByExec = allSalesPeople.map((s) => ({
    name: s.name,
    total: salaryAdvances.filter((a) => a.employeeName === s.name || a.employeeId === s.id).reduce((acc, a) => acc + (parseFloat(a.amount) || 0), 0),
  })).filter((row) => row.total > 0);
  const grandTotal = salaryAdvances.reduce((acc, a) => acc + (parseFloat(a.amount) || 0), 0);

  const inputCls = "w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500";

  return (
    <Card className="mt-6">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-slate-400" />Salary Advances · {month}
          </h4>
          <p className="text-xs text-slate-500 mt-0.5">Partial salary pulled mid-month. Deducts from the rep's net salary on Payroll. Visible to the rep on their My Attendance screen.</p>
        </div>
        {!adding && (
          <Btn variant="primary" size="sm" onClick={startAdd}>
            <Plus className="w-3.5 h-3.5" />Record advance
          </Btn>
        )}
      </div>

      {totalsByExec.length > 0 && (
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap bg-slate-50/60">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Month total:</span>
          <span className="text-xs font-semibold text-slate-900 tabular-nums">{inr(grandTotal)}</span>
          <span className="text-slate-300">|</span>
          {totalsByExec.map((row) => (
            <span key={row.name} className="text-xs bg-white rounded-md px-2 py-0.5 ring-1 ring-slate-200">
              {row.name.split(" ")[0]}: <span className="font-semibold text-red-700 tabular-nums">−{inr(row.total)}</span>
            </span>
          ))}
        </div>
      )}

      {adding && (
        <div className="px-5 py-4 border-b border-slate-200 bg-blue-50/30">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">Employees</label>
              <select value={draft.employeeId} onChange={(e) => setDraft({ ...draft, employeeId: e.target.value })} className={inputCls}>
                {allSalesPeople.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">Amount (₹)</label>
              <input type="number" min="0" value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                placeholder="e.g. 3000"
                className={inputCls + " tabular-nums"} autoFocus />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">Date</label>
              <input type="date" value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                className={inputCls + " tabular-nums"} />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">Note (optional)</label>
              <input value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                placeholder="Reason / memo"
                className={inputCls} />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <Btn variant="ghost" size="sm" onClick={cancel}>Cancel</Btn>
            <Btn variant="primary" size="sm" onClick={submit} disabled={!parseFloat(draft.amount) || parseFloat(draft.amount) <= 0}>
              <Check className="w-3.5 h-3.5" />{editing ? "Save changes" : "Record"}
            </Btn>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Employees</th>
              <th className="px-3 py-2.5 text-right">Amount</th>
              <th className="px-3 py-2.5">Note</th>
              <th className="px-3 py-2.5 w-24 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {salaryAdvances.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-400">
                No advances recorded this month. Click "Record advance" to add one.
              </td></tr>
            ) : salaryAdvances.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50">
                <td className="px-3 py-2.5 text-slate-600 tabular-nums">{a.date}</td>
                <td className="px-3 py-2.5 font-medium text-slate-900">{a.employeeName || allSalesPeople.find((s) => s.id === a.employeeId)?.name || "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-red-700">−{inr(a.amount)}</td>
                <td className="px-3 py-2.5 text-slate-600 text-xs">{a.note || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => startEdit(a)} title="Edit"
                      className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-colors">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => { if (confirm("Delete this advance?")) { deleteSalaryAdvance(a.id); toast("Advance deleted"); } }}
                      title="Delete"
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-md transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
